import { useCallback, useId, useRef, useState } from "react";
import type { Chair, HomeFigures, WaitlistEntry } from "@dentacore/shared-types";

import { formatAmounts } from "../patients/money";
import { WaitingBand } from "../plans/waiting-band";
import { AttendancePanel } from "./attendance-panel";
import { Elsewhere } from "./elsewhere";
import { WaitlistOffers } from "./waitlist-offers";
import styles from "./home.module.css";

type TabId = "needs" | "elsewhere" | "attendance";

/** Rows *Elsewhere* draws before *Show all* — its tab scrolls, so more than the old three. */
const ELSEWHERE_ROWS = 8;

/**
 * Home's right-hand card: **Needs you, Elsewhere, Attendance** — three tabs,
 * each carrying its count, so nothing hides without saying so (decision CS2).
 *
 * This is what keeps Home one screen tall on a busy day. The three used to be
 * stacked panels — plus *Still to book* above them and the waitlist beside
 * them — and on a full practice the page ran to one and a half screens. Only
 * the tab you are reading takes the height; the others still load, because
 * their counts are what tell you to look.
 *
 * **All three stay mounted**, hidden rather than unrendered, for the same
 * reason: an unmounted Elsewhere would fetch nothing and its tab would read
 * blank until clicked.
 *
 * **Needs you is open by default**: payments to collect, visits still to book
 * and the waitlist are the things the front desk acts on this morning.
 */
export function HomeTabs({
  branchId,
  day,
  currency,
  payments,
  onOpenPayments,
  waitlist,
  chairs,
  timezone,
  onChanged,
  onError,
}: {
  branchId: string | null;
  day: string;
  currency: string;
  payments: HomeFigures["paymentsToCollect"] | null;
  onOpenPayments: () => void;
  waitlist: readonly WaitlistEntry[];
  chairs: readonly Chair[];
  timezone: string;
  onChanged: () => void;
  onError: (message: string) => void;
}): React.JSX.Element {
  const [tab, setTab] = useState<TabId>("needs");
  const [toBook, setToBook] = useState(0);
  const [alerts, setAlerts] = useState<number | null>(null);
  const base = useId();
  const tabRefs = useRef<Record<TabId, HTMLButtonElement | null>>({
    needs: null,
    elsewhere: null,
    attendance: null,
  });

  const onToBook = useCallback((count: number) => setToBook(count), []);
  const onAlerts = useCallback((count: number) => setAlerts(count), []);

  const owed = payments?.count ?? 0;
  const needGroups = (owed > 0 ? 1 : 0) + (toBook > 0 ? 1 : 0) + (waitlist.length > 0 ? 1 : 0);

  const tabs: { id: TabId; label: string; count: string | null; hot: boolean }[] = [
    {
      id: "needs",
      label: "Needs you",
      count: needGroups > 0 ? String(needGroups) : null,
      hot: needGroups > 0,
    },
    {
      id: "elsewhere",
      label: "Elsewhere",
      count: alerts === null || alerts === 0 ? null : String(alerts),
      hot: false,
    },
    {
      id: "attendance",
      label: "Attendance",
      // **No number on the tab** (EB8). It carried the miss rate, so a good
      // week read "Attendance 0%" — as if nobody came. The chart says it.
      count: null,
      hot: false,
    },
  ];

  /** Left and right move between tabs, the WAI tabs pattern. */
  function onKeyDown(event: React.KeyboardEvent, index: number): void {
    const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = tabs[(index + step + tabs.length) % tabs.length];
    if (next === undefined) return;
    setTab(next.id);
    tabRefs.current[next.id]?.focus();
  }

  return (
    <section className={styles.tabsCard} data-testid="home-tabs">
      <div
        className={styles.tabList}
        role="tablist"
        aria-label="Needs you, elsewhere and attendance"
      >
        {tabs.map((item, index) => (
          <button
            key={item.id}
            ref={(element) => {
              tabRefs.current[item.id] = element;
            }}
            type="button"
            role="tab"
            id={`${base}-${item.id}-tab`}
            aria-selected={tab === item.id}
            aria-controls={`${base}-${item.id}`}
            tabIndex={tab === item.id ? 0 : -1}
            className={styles.tab}
            onClick={() => setTab(item.id)}
            onKeyDown={(event) => onKeyDown(event, index)}
            data-testid={`home-tab-${item.id}`}
          >
            {item.label}
            {item.count !== null && (
              <span className={item.hot ? styles.tabCountHot : styles.tabCount}>{item.count}</span>
            )}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`${base}-needs`}
        aria-labelledby={`${base}-needs-tab`}
        hidden={tab !== "needs"}
        className={styles.scroll}
        data-testid="needs-you"
      >
        {owed > 0 && payments !== null && (
          <section className={styles.need}>
            <div className={styles.needHead}>
              <span className={styles.needDot} data-tone="good" aria-hidden="true" />
              <span className={styles.needTitle}>
                {owed === 1 ? "1 payment to collect" : `${String(owed)} payments to collect`}
              </span>
              <span className={styles.needNote}>
                {formatAmounts(payments.outstanding, currency)} unpaid
              </span>
              <button
                type="button"
                className={styles.needAction}
                onClick={onOpenPayments}
                data-testid="needs-payments"
              >
                See who
              </button>
            </div>
          </section>
        )}

        <WaitingBand onCount={onToBook} />

        <WaitlistOffers
          waitlist={waitlist}
          chairs={chairs}
          timezone={timezone}
          onChanged={onChanged}
          onError={onError}
        />

        {needGroups === 0 && (
          <p className={styles.quiet} data-testid="needs-you-empty">
            Nothing needs you right now. Nobody is waiting for an earlier slot.
          </p>
        )}
      </div>

      <div
        role="tabpanel"
        id={`${base}-elsewhere`}
        aria-labelledby={`${base}-elsewhere-tab`}
        hidden={tab !== "elsewhere"}
        className={styles.scroll}
      >
        <Elsewhere branchId={branchId} inlineLimit={ELSEWHERE_ROWS} onCount={onAlerts} bare />
      </div>

      <div
        role="tabpanel"
        id={`${base}-attendance`}
        aria-labelledby={`${base}-attendance-tab`}
        hidden={tab !== "attendance"}
        className={styles.scroll}
      >
        <AttendancePanel branchId={branchId} day={day} bare />
      </div>
    </section>
  );
}
